import { FormatDateUtils } from '@agentnet/utils';
import { Typography, createStyles, makeStyles, Theme } from '@material-ui/core';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { KBLinks } from 'features/constants';
import { usePrivacyNoticeQuery } from 'features/help/queries/useFincenContentfulQuery';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { SummaryContentProps } from '../types';
import SummaryInfoItem from './components/SummaryInfoItem';
import { SUMMARY_SUBTITLE_ENTRY_ID } from './constants';

// Custom rendering options for Contentful rich text
const options = {
  renderMark: {
    [MARKS.BOLD]: (text: ReactNode) => <strong>{text}</strong>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_: any, children: ReactNode) => (
      <Typography variant="body2" component="p" style={{ marginBottom: 8 }}>
        {children}
      </Typography>
    ),
  },
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    summaryContainer: {
      backgroundColor: theme.palette.common.white,
      margin: theme.spacing(-2, 0, 3, 0),
      padding: theme.spacing(2, 4, 4, 4),
    },
    headerText: {
      padding: theme.spacing(3, 3, 2),
    },
    subtitle: {
      marginTop: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
  }),
);

const SummaryContent: FC<SummaryContentProps> = (props) => {
  const { data, status } = props;
  const classes = useStyles();
  const [subtitleContent, setSubtitleContent] = useState<any>(null);

  // Fetch summary subtitle content from Contentful
  useEffect(() => {
    const fetchSummarySubtitle = async () => {
      try {
        const result = await usePrivacyNoticeQuery(SUMMARY_SUBTITLE_ENTRY_ID);
        if (!result.error && result.data?.messageText?.json) {
          setSubtitleContent(result.data.messageText.json);
        }
      } catch (error) {
        console.error('Error fetching summary subtitle from Contentful:', error);
      }
    };

    fetchSummarySubtitle();
  }, []);

  if (!data) {
    return (
      <ContentContainer fullWidth mTop="0">
        <LoadingSpinner status="pending" variant="linear" />
      </ContentContainer>
    );
  }

  return (
    <>
      <PageHeader
        title="Summary"
        subtitle={subtitleContent ? documentToReactComponents(subtitleContent, options) : undefined}
        menuItems={[
          {
            label: 'Knowledge Base',
            link: KBLinks.fincenReport,
          },
        ]}
        data-testid="fincen-report-summary-header"
      />

      <div className={classes.summaryContainer} data-testid="fincen-report-summary-info">
        <SummaryInfoItem
          label="Created Date"
          value={data?.createdDate ? FormatDateUtils.MonthDateYear(data.createdDate) : ''}
          data-testid="fincen-report-summary-created-date"
        />

        <SummaryInfoItem
          label="Reporting Person"
          value={data?.reportingPerson ?? ''}
          data-testid="fincen-report-summary-reporting-person"
        />

        <SummaryInfoItem label="Property" value={data?.property ?? ''} data-testid="fincen-report-summary-property" />

        <SummaryInfoItem
          label="Transferee"
          value={data?.transferee ?? ''}
          data-testid="fincen-report-summary-transferee"
        />

        <SummaryInfoItem
          label="Transferor"
          value={data?.transferor ?? ''}
          data-testid="fincen-report-summary-transferor"
        />

        <SummaryInfoItem
          label="Transaction"
          value={data?.transaction ?? ''}
          data-testid="fincen-report-summary-transaction"
          isLast={true}
        />
      </div>
    </>
  );
};

export default React.memo(SummaryContent);
